//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import React, {
  useCallback, useEffect, useMemo, useState
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DatasetContext, type DatasetContextValue
} from '@/contexts/DatasetContext';
import { configService, datasetService } from '@/services';
import { type DatasetInfo, type RawGraphSchema } from '@/services/apiTypes';
import {
  datasetsQuery, useInvalidateDatasets, xgtKeys
} from '@/services/xgtQueries';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useAuth } from '@/hooks/useAuth';
import { getDisplayName } from '@/utils/FrameNames';

interface DatasetProviderProps {
  children: React.ReactNode;
}

// A dataset write whose endpoint returns the updated dataset list: the mutation
// seeds the cache straight from the response (no follow-up GET), and this
// exposes a plain (arg) => Promise<void> caller.  Called once per seeding write
// in the provider; factored out so each write is one line, not a useMutation +
// wrapper pair.
const useSeedingWrite = (
  mutationFn: (arg: string) => Promise<DatasetInfo[]>,
  seed: (data: DatasetInfo[]) => void,
) => {
  const { mutateAsync } = useMutation({ mutationFn, onSuccess: seed });
  return useCallback(
    async (arg: string): Promise<void> => { await mutateAsync(arg); },
    [mutateAsync]);
};

// Owns the active dataset selection and the dataset CRUD methods.  datasetsInfo
// is backed by the TanStack Query cache (key in xgtQueries) and auto-fetches
// once authed.  The active dataset is not stored as independent state — it is
// computed from the saved preference + the current list (see the memo below),
// so it stays valid automatically without a reconcile step.
const DatasetProvider = ({ children }: DatasetProviderProps) => {
  const handleError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const { activeUser, userProfile } = useAuth();

  // The user's last explicit dataset preference — a front-end mirror of
  // userProfile.activeDataset, seeded from it below.  Selecting a dataset
  // updates this and persists to the profile; the *effective* activeDataset is
  // computed from it (see the memo below), never stored independently.
  const [datasetPreference, setDatasetPreference] = useState('');
  // Freshness is one rule: poll every 60s while authed + tab visible, plus
  // immediate invalidation on in-app mutations (CRUD/upload/query/demo).  That
  // covers in-app and external changes uniformly, so no focus-refetch and no
  // refresh-on-navigation.  Polling pauses when the tab is hidden
  // (refetchIntervalInBackground defaults false); the computed activeDataset
  // re-derives on each refetch, never switching a still-valid selection.
  const {
    data: datasetsInfo = [], isLoading: datasetsLoading,
    isSuccess: datasetsLoaded
  } = useQuery({
    ...datasetsQuery(), enabled: !!activeUser,
    refetchInterval: 60_000, refetchOnWindowFocus: false,
  });

  // Demo-dataset loading state.  demoMenuItemDisabled is derived from the
  // dataset list + in-flight loads: a demo's menu item is disabled while it is
  // loading or once its data is present.  Recomputes whenever datasetsInfo
  // changes (mutation refetch, focus-refetch, demo-load refresh).
  const [demoIsLoading, setDemoIsLoading] =
      useState<Record<string, boolean>>({});
  const demoMenuItemDisabled = useMemo<Record<string, boolean>>(() => {
    // A demo graph is listed under its own name, or under the signed-in
    // user's when the server prefixes what they load.  The session user is
    // typed as a string or a token object, so the name comes from useAuth,
    // which has already resolved it to a string.
    const demoDataset = (name: string) =>
      datasetsInfo.find((d) => d.name === name) ||
      datasetsInfo.find((d) =>
        activeUser !== null && d.name === `${activeUser}_${name}`);
    // A frame by its bare name. A graph lists each member under its alias,
    // and a demo graph's aliases are the fully qualified frame names, so the
    // namespace has to come off before the name means anything -- by the
    // one rule the rest of the app strips it with.
    const frameNamed = <T extends { fully_qualified_name: string }>(
      frames: T[] | undefined, bare: string
    ): T | undefined =>
      frames?.find((f) => getDisplayName(f.fully_qualified_name) === bare);
    const ftDataset = demoDataset('FinTrans');
    const accounts = frameNamed(ftDataset?.vertices, 'Accounts');
    const transactions = frameNamed(ftDataset?.edges, 'Transactions');
    const nfDataset = demoDataset('AnonCyber');
    const devices = frameNamed(nfDataset?.vertices, 'Devices');
    const netflow = frameNamed(nfDataset?.edges, 'Netflow');
    return {
      fintrans: demoIsLoading.fintrans ||
                (accounts?.num_rows ?? 0) > 0 ||
                (transactions?.num_rows ?? 0) > 0,
      netflow: demoIsLoading.netflow ||
               (devices?.num_rows ?? 0) > 0 ||
               (netflow?.num_rows ?? 0) > 0,
    };
  }, [activeUser, datasetsInfo, demoIsLoading]);

  // The effective active dataset is COMPUTED, not stored: the saved preference
  // if it is still a real dataset, otherwise the first one (or '' if none).
  // Because it is derived, it can never be stale relative to the list — an
  // external delete, an in-app write, or a focus refetch just recomputes it, so
  // there is no reconcile state to keep in sync and the selector/schema views
  // never show a dataset that no longer exists.  A still-valid preference
  // always derives to itself, so a valid selection is never silently switched.
  // What a dataset has to be to be worth choosing: a topology.  The list itself holds
  // every namespace that holds anything, tables included (see choosableDatasets).
  //
  // Left out only where the answer says as much.  The frame arrays are optional by
  // contract -- not every response shape carries them -- and a payload that did not name
  // its frames is not evidence of a dataset without any, so one is offered unless it has
  // said it has neither.
  const withTopology = useMemo(() => datasetsInfo.filter(
    (d) => d.vertices == null || d.edges == null ||
      d.vertices.length > 0 || d.edges.length > 0), [datasetsInfo]);

  const activeDataset = useMemo(() => {
    // A saved preference is honoured whether or not it has a topology: it was chosen, and
    // the datasets page is where it was chosen from.
    const names = datasetsInfo.map((d) => d.name);
    if (datasetPreference && names.includes(datasetPreference))
      return datasetPreference;
    // Otherwise the first that can be worked on, rather than the first there is -- which
    // on a server with no graphs at all was a namespace holding one query's leftovers.
    return withTopology[0]?.name ?? '';
  }, [datasetsInfo, datasetPreference, withTopology]);

  // And whatever is active is offered, topology or not, so the selector always has an
  // option matching its value.
  const choosableDatasets = useMemo(() => {
    if (activeDataset === '' ||
        withTopology.some((d) => d.name === activeDataset)) return withTopology;

    const active = datasetsInfo.find((d) => d.name === activeDataset);
    return active == null ? withTopology : [...withTopology, active];
  }, [activeDataset, datasetsInfo, withTopology]);

  // Seed the local preference from the saved profile: login sets it, logout
  // clears it (userProfile goes null).  This only mirrors the persisted value —
  // validating it against the current list is the memo's job, not this
  // effect's.
  useEffect(() => {
    setDatasetPreference(userProfile?.activeDataset ?? '');
  }, [userProfile?.activeDataset]);

  // Two cache-update strategies for dataset writes (see the per-write usage
  // below).  Invalidate when the write starts an async job and returns a job
  // handle (createDataset here; demo-load/upload/query elsewhere): there is no
  // post-write list to seed, so mark the query stale and let it refetch once
  // the job lands.  The shared helper owns the key + semantics.  Each
  // dataset's schema rides inside the list payload (graph_schema), so
  // refreshing the list is also what refreshes every schema view.
  const invalidateDatasets = useInvalidateDatasets();

  // Seed when the write returns the updated dataset list (the clear/delete
  // ops): write the response straight into the cache, no follow-up GET.  Either
  // way the computed activeDataset re-derives from the new list, repairing the
  // selection if the active dataset was removed.  Some responses don't carry a
  // list though — dropping a standalone results frame comes back with an empty
  // body — and seeding a non-array would make every datasetsInfo.map/.find
  // throw and blank the whole app, so only seed a real array; otherwise fall
  // back to invalidating the query so it refetches the authoritative list.
  // `data` is a deserialized response that isn't always a list (see above), so
  // it's typed `unknown` and narrowed here rather than trusting a list shape
  // the guard exists precisely to reject.
  const seedDatasets = useCallback((data: unknown) => {
    if (Array.isArray(data))
      queryClient.setQueryData<DatasetInfo[]>(xgtKeys.datasets(), data);
    else
      invalidateDatasets();
  }, [queryClient, invalidateDatasets]);

  // createDataset returns a create-graph response, not the new list, so it
  // invalidates rather than seeding.  Returning the invalidate promise from
  // onSuccess makes mutateAsync resolve only after the refetch is queued, so
  // callers await the refresh, not just the write.
  const { mutateAsync: doCreateDataset } = useMutation({
    mutationFn: ({ schema, oldGraph }: {
      schema: RawGraphSchema; oldGraph?: string
    }) => handleError('createDataset',
      () => datasetService.createDataset(schema, oldGraph)),
    onSuccess: () => invalidateDatasets(),
  });
  const createDataset = useCallback(
    (schema: RawGraphSchema, oldGraph?: string) =>
      doCreateDataset({ schema, oldGraph }),
    [doCreateDataset]);

  // Not useSeedingWrite: this one answers with the frames the server would not
  // empty as well as the new list, and the caller needs those to report them.
  const { mutateAsync: doClearDataset } = useMutation({
    mutationFn: (dataset_name: string) => handleError(
      `clearDataset ${dataset_name}`,
      () => datasetService.clearDataset(dataset_name)),
    onSuccess: (result) => seedDatasets(result.datasets)
  });
  const clearDataset = useCallback(
    async (dataset_name: string): Promise<string[]> => {
      const result = await doClearDataset(dataset_name);
      return result.append_only_frames ?? [];
    }, [doClearDataset]);

  const clearFrame = useSeedingWrite(
    (frame_name) => handleError(
      `clearFrame ${frame_name}`,
      () => datasetService.clearFrame(frame_name)),
    seedDatasets);

  const deleteDataset = useSeedingWrite(
    (dataset_name) => handleError(
      `deleteDataset ${dataset_name}`,
      () => datasetService.deleteDataset(dataset_name)),
    seedDatasets);

  const deleteFrame = useSeedingWrite(
    (frame_name) => handleError(
      `deleteFrame ${frame_name}`,
      () => datasetService.deleteFrame(frame_name)),
    seedDatasets);

  // Unlike the other write methods, this does NOT refetchDatasets: the demo
  // load is asynchronous (it starts a job), so the dataset list only changes
  // once the job finishes.  The caller (useDemoDataLoader) polls asyncStatus
  // and invalidates the datasets query when the load completes.
  const createDemoData = useCallback(async (dataset_name: string) => {
    return await handleError(`createDemoData ${dataset_name}`,
      () => datasetService.createDemoData(dataset_name));
  }, [handleError]);

  // Persist a new preference even when it already matches the effective
  // fallback selection. Otherwise, adding a dataset can reorder the list and
  // silently move an unsaved fallback selection. The value must be either
  // cleared ('') or a real dataset.
  const setActiveDataset = useCallback(async (dataset: string) => {
    if (datasetPreference !== dataset &&
        (dataset === '' || datasetsInfo.some(d => d.name === dataset))) {
      await handleError(
        'updateUserProfile',
        () => configService.updateUserProfile({ activeDataset: dataset }));
      setDatasetPreference(dataset);
    }
  }, [datasetPreference, datasetsInfo, handleError]);

  const value = useMemo((): DatasetContextValue => ({
    activeDataset, setActiveDataset,
    datasetsInfo, choosableDatasets, datasetsLoading, datasetsLoaded,
    demoIsLoading, setDemoIsLoading, demoMenuItemDisabled,
    createDataset,
    clearDataset, clearFrame, deleteDataset, deleteFrame, createDemoData,
  }), [activeDataset, setActiveDataset, datasetsInfo, choosableDatasets,
      datasetsLoading, datasetsLoaded,
       demoIsLoading, demoMenuItemDisabled, createDataset,
       clearDataset, clearFrame, deleteDataset, deleteFrame, createDemoData]);

  return (
    <DatasetContext.Provider value={value}>
      {children}
    </DatasetContext.Provider>
  );
};

export default DatasetProvider;
